/**
 * @file: useSharingReportLogs.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { useCurrentUser } from '@euler/app/flows/auth/context';
import { kReportSharingLogsKey } from '@euler/functions/constants';
import { isDebugEnabled } from '@euler/functions/debug';
import { asyncStorageSubjectForKey$ } from '@euler/functions/useAsyncStorage';
import { AnalyticEvents } from '@euler/generated/AnalyticEvents';
import { ReportShareMethod } from '@euler/model';
import { useServiceFactory } from '@euler/services/factory';
import { makeDebug, onErrorIgnore } from '@euler/utils';
import produce from 'immer';
import { nanoid } from 'nanoid/non-secure';
import { useCallback, useRef } from 'react';
import { BehaviorSubject } from 'rxjs';
import invariant from 'tiny-invariant';
import { ReportSharingLog, reportShareTargetFromShareType } from './types';

export type ReportSharingLogStore = Record<string, ReportSharingLog[]>;

const debug = makeDebug('report-sharing-logs', isDebugEnabled('shareReport'));

export const kDefaultReportSharingLogStore: ReportSharingLogStore = {};

export const useReportSharingLogStore$ = () => {
  return useRef(
    asyncStorageSubjectForKey$<ReportSharingLogStore>(
      kReportSharingLogsKey,
      kDefaultReportSharingLogStore,
    ),
  ).current as BehaviorSubject<ReportSharingLogStore>;
};

export const makeReportSharingLog = (
  params: Omit<ReportSharingLog, 'id'>,
): ReportSharingLog => {
  const id = nanoid();
  return { id, ...params };
};

export const useReportSharingLogManager = () => {
  const userId = useCurrentUser()?.user.uid;
  const store$ = useReportSharingLogStore$();
  const { orderService } = useServiceFactory();

  const appendLog = useCallback(
    (log: ReportSharingLog) => {
      invariant(
        log.shareMethod === ReportShareMethod.WeixinMiniprogram ||
          log.shareMethod === ReportShareMethod.WecomMiniprogram,
        'Only weixin or wework sharing logs are supported to be manged this way. ',
      );
      if (!userId) return;
      const updated = produce(store$.value, draft => {
        const logs = draft[userId] ?? [];
        logs.push(log);
        draft[userId] = logs;
      });
      store$.next(updated);
      debug('appended new log: %O', log);
    },
    [store$, userId],
  );

  const commitLog = useCallback(
    async (id: string) => {
      if (!userId) return;

      const logs = store$.value[userId];
      if (logs == null) return;

      const index = logs.findIndex(x => x.id === id);
      const log = logs[index];

      if (log == null) return;

      if (log.shareMethod === ReportShareMethod.WeixinMiniprogram) {
        AnalyticEvents.default.reportSharedViaWeixin(log.eventArgs);
      } else if (log.shareMethod === ReportShareMethod.WecomMiniprogram) {
        AnalyticEvents.default.reportSharedViaWework(log.eventArgs);
      }

      await orderService
        .logReportSharing(log.orderNo, {
          reportType: log.reportType,
          shareMethod: log.shareMethod,
          shareTarget: reportShareTargetFromShareType(log.eventArgs.shareType),
        })
        .catch(onErrorIgnore);

      const updated = produce(store$.value, draft => {
        draft[userId]?.splice(index, 1);
      });
      store$.next(updated);

      debug('committed log: %O', log);
    },
    [orderService, store$, userId],
  );

  const rollbackLog = useCallback(
    (id: string) => {
      if (!userId) return;

      const logs = store$.value[userId];
      if (logs == null) return;

      const index = logs.findIndex(x => x.id === id);
      const log = logs[index];

      if (log == null) return;

      if (log.shareMethod === ReportShareMethod.WeixinMiniprogram) {
        AnalyticEvents.default.reportShareViaWeixinCanceled(log.eventArgs);
      } else if (log.shareMethod === ReportShareMethod.WecomMiniprogram) {
        AnalyticEvents.default.reportShareViaWeworkCanceled(log.eventArgs);
      }

      const updated = produce(store$.value, draft => {
        draft[userId]?.splice(index, 1);
      });
      store$.next(updated);

      debug('rollback log: %O', log);
    },
    [store$, userId],
  );

  const flushLogs = useCallback(async () => {
    if (!userId) return;

    const logs = store$.value[userId];
    if (!logs?.length) {
      debug.verbose('no sharing logs to flush');
      return;
    }

    for (const log of logs) {
      if (log.shareMethod === ReportShareMethod.WeixinMiniprogram) {
        AnalyticEvents.default.reportSharedViaWeixin(log.eventArgs);
      } else if (log.shareMethod === ReportShareMethod.WecomMiniprogram) {
        AnalyticEvents.default.reportSharedViaWework(log.eventArgs);
      }

      await orderService
        .logReportSharing(log.orderNo, {
          reportType: log.reportType,
          shareMethod: log.shareMethod,
          shareTarget: reportShareTargetFromShareType(log.eventArgs.shareType),
        })
        .catch(onErrorIgnore);
    }

    const updated = produce(store$.value, draft => {
      draft[userId] = [];
    });
    store$.next(updated);

    debug('flushed %d logs', logs.length);
  }, [orderService, store$, userId]);

  return { appendLog, commitLog, rollbackLog, flushLogs };
};
